<template>
	<div v-if="isExpanded" class="fadein">
		<div class="flex wrap flex-between xs-12 p2 bg7--l2 b1 bc7 mt2 r2">
			<div class="xs-12 pl2 pr2 mt2 mb2 fw7">{{ $t("filter") }}</div>

			<!-- Dynamically add filter fields -->
			<div
				class="flex xs-12 lg-4 mt2 mb2 pl2 pr2"
				v-for="(item, i) in fieldsToRender"
				:key="i"
			>
				<div class="xs-12 bg5--a70 p4 r2">
					<component
						:is="item.component"
						size="small"
						:label="item.label"
						v-model="payload[item.model]"
					/>
				</div>
			</div>

			<div class="flex xs-12 xs-left mt2 pl2 pr2">
				<j-button
					size="small"
					type="primary"
					@click="handleFilterSubmit"
					class="mr2"
					action="submit"
					>{{
						submitButtonText ? submitButtonText : this.$t("apply_filters")
					}}</j-button
				>
				<j-button size="small" @click="handleReset" type="secondary">{{
					resetButtonText ? resetButtonText : this.$t("clear_filters")
				}}</j-button>
			</div>
		</div>
	</div>
</template>

<script>
import JBaseInputText from "@/components/JBaseInputText/"
import CoreSelectClient from "@/modules/core/components/CoreSelectClient/"
import JBaseLabel from "@/components/JBaseLabel/"
import JBaseRadios from "@/components/JBaseRadios/"
import JBaseSelect from "@/components/JBaseSelect/"
import FieldDate from "./FieldDate.vue"
import FieldPolicyStatus from "./FieldPolicyStatus.vue"
import FieldQuoteStatus from "./FieldQuoteStatus.vue"
import FieldText from "./FieldText.vue"
import FieldClients from "./FieldClients.vue"
import FieldSources from "./FieldSources.vue"
import FieldInsuranceTypes from "./FieldInsuranceTypes.vue"
import FieldDatePicker from "./FieldDatePicker.vue"
import JButton from "@/components/JButton/"
import { clone, transformDate } from "@/utils/"
import { mapActions } from "vuex"
import dayjs from "dayjs"

/**
 * Initial state for all possible filter fields
 */
const initialState = {
	createdAt: { type: "between", before: null, after: null }, // date format for before, after is DD/MM/YYYY
	startDate: { type: "between", before: null, after: null },
	endDate: { type: "between", before: null, after: null },
	issueDate: { type: "between", before: null, after: null },
	policyNumber: null,
	policyStatus: "all",
	quoteStatus: "all",
	insuranceTypes: [],
	sources: [],
	clientName: null,
	clientEmail: null,
	template: null
}

export default {
	components: {
		JBaseInputText,
		CoreSelectClient,
		JBaseLabel,
		JBaseRadios,
		JBaseSelect,
		FieldDate,
		FieldPolicyStatus,
		FieldQuoteStatus,
		FieldText,
		FieldClients,
		FieldSources,
		FieldInsuranceTypes,
		FieldDatePicker,
		JButton
	},

	data() {
		return {
			payload: clone(initialState)
		}
	},

	props: {
		value: {
			type: Object,
			required: false,
			default: () => clone(initialState)
		},

		for: {
			type: String,
			required: false,
			default: "policies"
		},

		isExpanded: {
			type: Boolean,
			default: false,
			required: false
		},

		submitButtonText: {
			type: String,
			default: null
		},

		resetButtonText: {
			type: String,
			default: null
		},

		forceReset: {
			type: Number,
			default: 0,
			required: false
		}
	},

	computed: {
		fieldsToRender() {
			if (
				this.for === "policies" ||
				this.for === "commissions" ||
				this.for === "policies_notes"
			)
				return [
					{
						component: "field-text",
						model: "policyNumber",
						label: this.$t("policy_number")
					},
					{
						component: "field-date",
						model: "createdAt",
						label: this.$t("created")
					},
					{
						component: "field-policy-status",
						model: "policyStatus"
					},
					{
						component: "field-date",
						model: "startDate",
						label: this.$t("start_date")
					},
					{
						component: "field-date",
						model: "endDate",
						label: this.$t("end_date")
					},
					{
						component: "field-date",
						model: "issueDate",
						label: this.$t("issue_date")
					},
					//TODO: changes in api, disabling cause does not work
					// {
					// 	component: "field-insurance-types",
					// 	model: "insuranceTypes",
					// 	label: this.$t("insurance_type")
					// },
					{
						component: "field-sources",
						model: "sources"
					}
				]

			if (this.for === "quotes" || this.for === "quotes_notes")
				return [
					{
						component: "field-text",
						model: "quoteNumber",
						label: this.$t("quote_number")
					},
					{
						component: "field-date",
						model: "createdAt",
						label: this.$t("created")
					},
					{
						component: "field-quote-status",
						model: "quoteStatus"
					},
					{
						component: "field-date",
						model: "startDate",
						label: this.$t("start_date")
					},
					{
						component: "field-date",
						model: "endDate",
						label: this.$t("end_date")
					},
					{
						component: "field-date",
						model: "issueDate",
						label: this.$t("issue_date")
					},
					{
						component: "field-insurance-types",
						model: "insuranceTypes",
						label: this.$t("insurance_type")
					},
					{
						component: "field-sources",
						model: "sources"
					}
				]

			if (this.for === "individuals" || this.for === "companies")
				return [
					{
						component: "field-text",
						model: "clientName",
						label: this.$t("name")
					},
					{
						component: "field-date",
						model: "createdAt",
						label: this.$t("created")
					},
					{
						component: "field-text",
						model: "clientEmail",
						label: this.$t("email")
					}
				]

			if (this.for === "vhis")
				return [
					{
						component: "field-date",
						model: "createdAt",
						label: this.$t("created")
					},
					{
						component: "field-date",
						model: "startDate",
						label: this.$t("start_date")
					},
					{
						component: "field-date",
						model: "endDate",
						label: this.$t("end_date")
					},
					{
						component: "field-date",
						model: "issueDate",
						label: this.$t("issue_date")
					}
				]

			if (this.for === "abc_risk_london")
				return [
					{
						component: "field-date-picker",
						model: "issueDate",
						label: "Transaction Issue Date"
					}
				]

			if (this.for === "abc_risk")
				return [
					{
						component: "field-date-picker",
						model: "issueDate",
						label: "Transaction Issue Date"
					}
				]

			if (this.for === "abc_premium_bordereaux")
				return [
					{
						component: "field-date-picker",
						model: "issueDate",
						label: "Transaction Issue Date"
					}
				]

			if (this.for === "abc_debit_note")
				return [
					{
						component: "field-date-picker",
						model: "issueDate",
						label: "Transaction Issue Date"
					}
				]

			if (this.for === "abc_claim")
				return [
					{
						component: "field-date-picker",
						model: "createdAt",
						label: "Claim Create Date"
					}
				]

			if (this.for === "abc_claim_bordereaux")
				return [
					{
						component: "field-date-picker",
						model: "createdAt",
						label: "Claim Create Date"
					}
				]

			if (this.for === "abc_claims_paid")
				return [
					{
						component: "field-date-picker",
						model: "createdAt",
						label: "Claim Create Date"
					}
				]

			if (this.for === "abc_claims_outstanding")
				return [
					{
						component: "field-date-picker",
						model: "createdAt",
						label: "Claim Create Date"
					}
				]

			if (this.for === "abc_policy_renewal")
				return [
					{
						component: "field-date-picker",
						model: "issueDate",
						label: "Policy Issue Date"
					}
				]

			return []
		}
	},

	methods: {
		...mapActions("clients", { clientsReadAll: "readAll" }),
		/**
		 * on submit we emit event with 2 objects
		 * 1 payload - is an actual payload of the filter form
		 * 2 where - is processed payload into where argument for graphql query
		 *
		 * In order to save filled in filter, you should save payload (e.g. into store)
		 */
		handleFilterSubmit() {
			this.$emit("submit", {
				payload: this.payload,
				where: this.composeWhere()
			})
			//Reset to first page everytime filter button is pressed on Clients Page
			if (this.for === "individuals") {
				this.$store.commit("clients/SET_PAGE", 1)
				this.clientsReadAll()
			}
		},

		handleReset() {
			this.payload = clone(initialState)
		},

		/**
		 * This method will compose `where` param for graphql query
		 * It returns where object based on what user added in filter
		 */
		composeWhere() {
			// Receives payload of field-date component
			// Crunches it and produces proper where object
			const crunchDate = (
				what,
				{ type = null, before = null, after = null }
			) => {
				//parse user input DD/MM/YYYY to YYYY-MM-DD
				before = transformDate(before)
				after = transformDate(after)

				//if date filters are submitted empty ALWAYS returns type_in:CUSTOMER for Clients
				if (!before && !after) {
					if (this.for === "individuals" || this.for === "companies")
						return { type_in: ["CUSTOMER"] }
					else return {}
				}

				// between case
				if (type === "between") {
					return {
						//add 23 hours 59 minutes to before
						and: [
							{
								[`${what}_gt`]: dayjs(after).format("YYYY-MM-DDTHH:mm:ssZ")
							},
							{
								[`${what}_lt`]: dayjs(before)
									.add(23, "hour")
									.add(59, "minute")
									.format("YYYY-MM-DDTHH:mm:ssZ")
							}
						]
					}
				}

				// after case
				if (type === "after") {
					return {
						[`${what}_gt`]: dayjs(after)
							.add(23, "hour")
							.add(59, "minute")
							.format("YYYY-MM-DDTHH:mm:ssZ")
					}
				}

				// before case
				if (type === "before") {
					return {
						[`${what}_lt`]: dayjs(before).format("YYYY-MM-DDTHH:mm:ssZ")
					}
				}
			}

			let where = { and: [] }

			// policyNumber
			if (this.payload.policyNumber)
				where.and.push({
					issuerNumber_contains: this.payload.policyNumber
				})

			//quoteNumber
			if (this.payload.quoteNumber)
				where.and.push({
					id: this.payload.quoteNumber
				})

			// policyStatus - inlcude only for policies
			if (
				this.for === "policies" ||
				this.for === "policies_notes" ||
				this.for === "premium_bordereaux"
			)
				where.and.push({
					status_in:
						this.payload.policyStatus === "all"
							? ["ISSUED", "CANCELLED"]
							: [this.payload.policyStatus]
				})

			// quoteStatus - inlcude only for quotes
			if (this.for === "quotes" || this.for === "quotes_notes")
				where.and.push({
					status_in:
						this.payload.quoteStatus === "all"
							? ["QUOTING", "REJECTED"]
							: [this.payload.quoteStatus]
				})

			// insuranceTypes
			if (this.payload.insuranceTypes.length > 0)
				where.and.push({
					productId: {
						type_in: this.payload.insuranceTypes.map(item => item.value)
					}
				})

			// clientName
			if (this.payload.clientName)
				where.and.push({
					name_contains: this.payload.clientName
				})

			// clientEmail
			if (this.payload.clientEmail)
				where.and.push({
					email_contains: this.payload.clientEmail
				}) // for some reason email doesn't work with regex

			// sources
			if (this.payload.sources.length > 0)
				where.and.push({
					source_in: this.payload.sources.map(item => item.value)
				})

			// startDate
			if (this.payload.startDate)
				where.and.push(crunchDate("startDate", this.payload.startDate))

			// endDate
			if (this.payload.endDate)
				where.and.push(crunchDate("endDate", this.payload.endDate))

			// issueDate
			if (this.payload.issueDate)
				where.and.push(crunchDate("issueDate", this.payload.issueDate))

			// createdAt
			if (this.payload.createdAt)
				console.log(crunchDate("createdAt", this.payload.createdAt))
			where.and.push(crunchDate("createdAt", this.payload.createdAt))

			return where
		}
	},

	watch: {
		value: {
			handler(newValue) {
				// If value is not null, populate internal state
				if (newValue) this.payload = clone(newValue)
			},
			deep: true,
			immediate: true
		},

		// When forceReset changes, reset the form
		forceReset: {
			handler() {
				this.handleReset()
			},
			immediate: false
		}
	}
}
</script>

<style scoped>
@import "../../../../assets/css/tokens.css";

.fadein {
	animation: fadein 300ms ease-out;
}
</style>
